import Image, { ImageLoaderProps, ImageProps } from 'next/image';
import { forwardRef } from 'react';
import { UH_STATIC_URL, SPEEDSIZE_CDN } from 'lib/config/assetUrls';

const SPEEDSIZE_CLIENT_ID = process.env.NEXT_PUBLIC_SPEEDSIZE_CLIENT_ID;

const baseUrl = (src: string) =>
  `${SPEEDSIZE_CDN}/${SPEEDSIZE_CLIENT_ID}/${UH_STATIC_URL}/${src}`;

export const speedsizeLoader = ({ src, width }: ImageLoaderProps): string => {
  const base = baseUrl(src);
  // srcs already carrying a /w_<n> (or /w_<n>,h_<n>) suffix must pass
  // through — a double suffix 404s.
  if (!width || /\/w_\d+(,h_\d+)?$/i.test(src)) return base;
  // the extension check tolerates a trailing ?v=<n> cache-buster; the CDN
  // accepts the width segment after the query (.png?v=3/w_640)
  if (!/\.(png|jpe?g|webp)(\?v=\d+)?$/i.test(src)) return base;
  return `${base}/w_${width}`;
};
const baseLoader = ({ src }: ImageLoaderProps): string => baseUrl(src);

// CDN URL for an <img>/<picture> bypassing next/image; `width` appends /w_<n> like speedsizeLoader.
export const getSpeedsizeImageUrl = (src: string, width?: number): string => {
  if (src.startsWith('/')) return src;
  const base = baseUrl(src);
  return width && /\.(png|jpe?g|webp)$/i.test(src) ? `${base}/w_${width}` : base;
};

export const CustomImage = forwardRef(function CustomImage(
  { src, alt, ...props }: ImageProps,
  ref: React.Ref<HTMLImageElement>,
) {
  // Leading-slash string paths are local `/public` assets (Next.js
  // convention). Serve them as-is: prefixing the speedsize CDN URL would
  // yield an invalid double-slash URL, and the on-demand optimizer stalls on
  // them in dev, so they skip it (they are exported at their display size).
  const isLocalPublicPath = typeof src === 'string' && src.startsWith('/');
  return (
    <Image
      ref={ref}
      src={src}
      loader={
        isLocalPublicPath
          ? undefined
          : props.priority
          ? baseLoader
          : speedsizeLoader
      }
      unoptimized={isLocalPublicPath || props.unoptimized}
      alt={alt}
      {...props}
    />
  );
});
